import {
    Address,
    Intermediary,
    Representative,
    Location,
    Profile,
    Branch,
    Contract,
    CommissionSystem,
    CommissionLine,
    CommissionGroup,
    Commission,
    UserBasicEntity,
    Acl,
    SearchEntityTypeEnum,
} from "view360Card/models";
import { AddressDto } from "view360Card/service/dtos/AddressDto";
import { BranchDto } from "view360Card/service/dtos/BranchDto";
import { ContractDto } from "view360Card/service/dtos/ContractDto";
import { IntermediaryDto } from "view360Card/service/dtos/IntermediaryDto";
import { LocationDto } from "view360Card/service/dtos/LocationDto";
import { ProfileDto } from "view360Card/service/dtos/ProfileDto";
import { RepresentativeDto } from "view360Card/service/dtos/RepresentativeDto";
import { DynamicAttributeFromResponse, sortAndFilterDynamicAttributes } from "./DynamicAttributeFromResponse";
import orderBy from "lodash/orderBy";
import { arrayUtils } from "ecom-teams-apps-core";
import { CommissionDto } from "view360Card/service/dtos/CommissionDto";
import { DictionaryItemFromResponse } from "./DictionaryItemFromResponse";
import { DictionaryItemFromDefinedResponse } from "./DictionaryItemFromDefinedResponse";
import { UserBasicEntityDto } from "view360Card/service/dtos/UserBasicEntityDto";
import { AclDto } from "view360Card/service/dtos/AclDto";

export class IntermediaryFromResponse {
    constructor(private dto: IntermediaryDto) {}

    domainModel(): Intermediary {
        return {
            id: this.dto.id,
            number: this.dto.number,
            status: new DictionaryItemFromResponse(this.dto.status).domainModel(),
            name: this.dto.name,
            branch: new BranchFromResponse(this.dto.branch).domainModel(),
            contractList: this.sortContracts(
                (this.dto.contractList ?? []).map((i) => {
                    return new ContractFromResponse(i).domainModel();
                })
            ),
            director: new UserBasicEntityFromResponse(this.dto.director).domainModel(),
            salesManager: new UserBasicEntityFromResponse(this.dto.salesManager).domainModel(),
            startEHM: this.dto.startEHM ? new Date(this.dto.startEHM) : undefined,
            startEHZ: this.dto.startEHZ ? new Date(this.dto.startEHZ) : undefined,
            address: new AddressFromResponse(this.dto.address).domainModel(),
            email: this.dto.email,
            phone: this.dto.phone,
            representativeList: this.sortRepresentatives(
                (this.dto.representativeList ?? []).map((i) => {
                    return new RepresentativeFromResponse(i).domainModel();
                })
            ),
            locationList: this.sortLocations(
                (this.dto.locationList ?? []).map((i) => {
                    return new LocationFromResponse(i).domainModel();
                })
            ),
            profileList: this.sortProfiles(
                (this.dto.profileList ?? []).map((i) => {
                    return new ProfileFromResponse(i).domainModel();
                })
            ),
            dynamicAttributeList: sortAndFilterDynamicAttributes(
                (this.dto.dynamicAttributeList ?? []).map((i) => {
                    return new DynamicAttributeFromResponse(i).domainModel();
                })
            ),
            commissionSystemList: new CommissionSystemFromResponse(this.dto.commissionList).domainModel(),
            nip: this.dto.nip,
            regon: this.dto.regon,
            businessIdentifier: this.dto.number,
            acl: new EntityAclFromResponse(this.dto.acl).domainModel(),
            entityType: SearchEntityTypeEnum.Intermediary,
        };
    }

    private sortRepresentatives(representatives: Representative[]) {
        return orderBy(representatives, [(r) => r.isOwner], ["desc"]);
    }

    private sortLocations(locations: Location[]) {
        return orderBy(locations, [(location) => location.type?.orderIndex]);
    }

    private sortContracts(contracts: Contract[]) {
        return orderBy(contracts, [(contract) => contract.endDate, (contract) => contract.startDate], ["desc", "desc"]);
    }

    private sortProfiles(profiles: Profile[]) {
        return orderBy(profiles, [
            (profile) => profile.branch?.name?.toLocaleLowerCase(),
            (profile) => profile.rms?.fullName?.toLowerCase(),
            (profile) => profile.tenantCode?.toLocaleLowerCase(),
        ]);
    }
}

export class UserBasicEntityFromResponse {
    constructor(private dto: UserBasicEntityDto | undefined) {}

    domainModel(): UserBasicEntity | undefined {
        if (!this.dto) {
            return undefined;
        }

        return {
            username: this.dto.username,
            firstName: this.dto.firstName,
            lastName: this.dto.lastName,
            fullName: this.dto.fullName,
            mobilePhone: this.dto.mobilePhone,
            userPrincipalName: this.dto.userPrincipalName,
        };
    }
}

export class EntityAclFromResponse {
    constructor(private dto: AclDto | undefined) {}

    domainModel(): Acl | undefined {
        if (!this.dto) {
            return undefined;
        }

        return {
            commissionTable: this.dto.commissionTable,
            contracts: this.dto.contracts,
            employees: this.dto.employees,
            salesReports: this.dto.salesReports,
            notes: this.dto.notes,
        };
    }
}

export class AddressFromResponse {
    constructor(private dto: AddressDto | undefined) {}

    domainModel(): Address | undefined {
        if (!this.dto) {
            return undefined;
        }

        return {
            postalCode: this.dto.postalCode,
            streetName: this.dto.streetName,
            streetNumber: this.dto.streetNumber,
            apartmentNumber: this.dto.apartmentNumber,
            city: this.dto.city,
        };
    }
}

export class RepresentativeFromResponse {
    constructor(private dto: RepresentativeDto) {}

    domainModel(): Representative {
        return {
            id: this.dto.id,
            sourceCode: this.dto.sourceCode,
            sourceRepresentativeId: this.dto.sourceRepresentativeId,
            punu: this.dto.punu,
            firstName: this.dto.firstName,
            lastName: this.dto.lastName,
            isOwner: this.dto.isOwner,
            salesPersonId: this.dto.salesPersonId,
        };
    }
}

export class LocationFromResponse {
    constructor(private dto: LocationDto) {}

    domainModel(): Location {
        return {
            id: this.dto.id,
            sourceCode: this.dto.sourceCode,
            sourceLocationId: this.dto.sourceLocationId,
            type: new DictionaryItemFromResponse(this.dto.type).domainModel(),
            tagList: (this.dto.tagList ?? []).map((i) => {
                return new DictionaryItemFromDefinedResponse(i).domainModel();
            }),
            address: new AddressFromResponse(this.dto.address).domainModel(),
        };
    }
}

export class ProfileFromResponse {
    constructor(private dto: ProfileDto) {}

    domainModel(): Profile {
        return {
            id: this.dto.id,
            number: this.dto.number,
            isMain: this.dto.isMain,
            tenantCode: this.dto.tenantCode,
            channel: new DictionaryItemFromResponse(this.dto.channel).domainModel(),
            distributionGroup: new DictionaryItemFromResponse(this.dto.distributionGroup).domainModel(),
            proxy: new DictionaryItemFromResponse(this.dto.proxy).domainModel(),
            branch: new BranchFromResponse(this.dto.branch).domainModel(),
            rms: new UserBasicEntityFromResponse(this.dto.rms).domainModel(),
            status: new DictionaryItemFromResponse(this.dto.status).domainModel(),
        };
    }
}

export class BranchFromResponse {
    constructor(private dto: BranchDto | undefined) {}

    domainModel(): Branch | undefined {
        if (!this.dto) {
            return undefined;
        }
        return {
            id: this.dto.id,
            code: this.dto.code,
            name: this.dto.name,
        };
    }
}

export class ContractFromResponse {
    constructor(private dto: ContractDto) {}

    domainModel(): Contract {
        return {
            id: this.dto.id,
            endDate: this.dto.endDate ? new Date(this.dto.endDate) : undefined,
            startDate: this.dto.startDate ? new Date(this.dto.startDate) : undefined,
            status: new DictionaryItemFromResponse(this.dto.status).domainModel(),
            type: new DictionaryItemFromResponse(this.dto.type).domainModel(),
            period: new DictionaryItemFromResponse(this.dto.period).domainModel(),
        };
    }
}

export class CommissionSystemFromResponse {
    constructor(private dto: CommissionDto[] | undefined) {}

    domainModel(): CommissionSystem[] {
        if (!this.dto) {
            return [];
        }

        const result: CommissionSystem[] = [];
        const grouped = arrayUtils.groupBy<CommissionDto>(this.dto, (c) => c.system?.code);
        var keysIterator = grouped.keys();
        let key = keysIterator.next().value;
        while (key !== undefined) {
            var commissionsBySystem: CommissionDto[] = grouped.get(key);
            var anySystem = commissionsBySystem[0].system!;
            result.push({
                code: anySystem.code,
                description: anySystem.description,
                id: anySystem.id,
                orderIndex: anySystem.orderIndex,
                lineList: new CommissionLineFromResponse(commissionsBySystem).domainModel(),
            });
            key = keysIterator.next().value;
        }

        const orderded = orderBy(result, [(r) => r.orderIndex]);
        return orderded;
    }
}

export class CommissionLineFromResponse {
    constructor(private dto: CommissionDto[] | undefined) {}

    domainModel(): CommissionLine[] {
        if (!this.dto) {
            return [];
        }

        const result: CommissionLine[] = [];
        const grouped = arrayUtils.groupBy<CommissionDto>(this.dto, (c) => c.line?.code);
        var linesIterator = grouped.keys();
        let line = linesIterator.next().value;
        while (line !== undefined) {
            var commissionsByLine: CommissionDto[] = grouped.get(line);
            var anyLine = commissionsByLine[0].line!;
            result.push({
                code: anyLine.code,
                description: anyLine.description,
                id: anyLine.id,
                orderIndex: anyLine.orderIndex,
                groupList: new CommissionGroupFromResponse(commissionsByLine).domainModel(),
            });
            line = linesIterator.next().value;
        }
        return orderBy(result, (r) => r.orderIndex);
    }
}

export class CommissionGroupFromResponse {
    constructor(private dto: CommissionDto[] | undefined) {}

    domainModel(): CommissionGroup[] {
        if (!this.dto) {
            return [];
        }

        const result: CommissionGroup[] = [];
        const grouped = arrayUtils.groupBy<CommissionDto>(this.dto, (c) => c.group?.code);
        var groupsIterator = grouped.keys();
        let group = groupsIterator.next().value;
        while (group !== undefined) {
            var commissionsByLine: CommissionDto[] = grouped.get(group);
            var anyLine = commissionsByLine[0].group!;

            result.push({
                code: anyLine.code,
                description: anyLine.description,
                id: anyLine.id,
                orderIndex: anyLine.orderIndex,
                commissionList: orderBy(
                    commissionsByLine.map((i) => {
                        return new CommissionFromResponse(i).domainModel();
                    }),
                    (c) => c.orderIndex
                ),
            });
            group = groupsIterator.next().value;
        }

        return orderBy(result, (r) => r.orderIndex);
    }
}

export class CommissionFromResponse {
    constructor(private dto: CommissionDto) {}

    domainModel(): Commission {
        return {
            baseRate: this.dto.baseRate,
            claimRatioRate: this.dto.claimRatioRate,
            dynamicsRate: this.dto.dynamicsRate,
            locationRate: this.dto.locationRate,
            id: this.dto.id,
            orderIndex: this.dto.orderIndex,
            yearGroup: this.dto.yearGroup,
            sumRate:
                this.dto.sumRate ??
                (this.dto.baseRate ?? 0) +
                    (this.dto.claimRatioRate ?? 0) +
                    (this.dto.dynamicsRate ?? 0) +
                    (this.dto.locationRate ?? 0),
        };
    }
}
